import classnames from 'classnames';
import SortedHostProvider from 'modules/experience/providers/SortedHostProvider';
import React, { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { getHost } from 'utils/firstHost';

import { ExperienceSource } from '../../model/IExperience';
import Image from '../Image';
import ExperienceCardAvatarLabel from './ExperienceCardAvatarLabel';

const CSS = require('./ExperienceCardAvatar.module.scss');

interface ExperienceCardAvatarProps {
  experience: ExperienceSource;
  forceHost?: string;
  label?: string;
}

const AVATAR_OPTIONS = { width: 64, height: 64 };
const INVIEW_OPIONS = { triggerOnce: true, threshold: 0, delay: 140 };

function OriginalsAvatars({
  experience,
  label,
  forceHost,
}: ExperienceCardAvatarProps) {
  const [firstLoaded, setFirstLoaded] = useState(false);
  const onFirstLoaded = () => setFirstLoaded(true);
  const [secondLoaded, setSecondLoaded] = useState(false);
  const onSecondLoaded = () => setSecondLoaded(true);

  const { ref, inView } = useInView(INVIEW_OPIONS);
  const classesSecondhost = classnames(
    CSS.experienceCardAvatarSecond,
    CSS.experienceCardAvatar,
    {
      [CSS.experienceCardAvatarSecondAnimate]:
        inView && firstLoaded && secondLoaded,
    },
  );

  return (
    <SortedHostProvider
      experienceId={experience.id}
      hosts={experience.hosts}
      highlightedHost={forceHost}
    >
      {({ sortedHosts }) => {
        return (
          <div className={CSS.experienceCardAvatarContainer} ref={ref}>
            <div className={CSS.experienceCardAvatar}>
              <Image
                animate
                onLoad={onFirstLoaded}
                className={CSS.experienceCardAvatarImage}
                src={sortedHosts[0].photo}
                width={AVATAR_OPTIONS.width}
                height={AVATAR_OPTIONS.height}
                options={AVATAR_OPTIONS}
                loading="lazy"
              />
            </div>
            <div className={classesSecondhost}>
              <Image
                animate
                onLoad={onSecondLoaded}
                className={CSS.experienceCardAvatarImage}
                src={sortedHosts[1].photo}
                width={AVATAR_OPTIONS.width}
                height={AVATAR_OPTIONS.height}
                options={AVATAR_OPTIONS}
                loading="lazy"
              />
            </div>
            <ExperienceCardAvatarLabel
              text={label}
              animate={inView && firstLoaded && secondLoaded}
              nrOfHosts={sortedHosts.length}
            />
          </div>
        );
      }}
    </SortedHostProvider>
  );
}

function ExperienceCardAvatar({
  experience,
  label,
  forceHost,
}: ExperienceCardAvatarProps) {
  const amountOfHosts = experience.hosts ? experience.hosts.length : 0;

  if (!experience.is_originals || amountOfHosts < 2) {
    const host = getHost(experience.hosts, experience.account, forceHost);
    const photo = host.photo;
    return (
      <div className={CSS.experienceCardAvatarContainer}>
        <Image
          animate
          alt="host image"
          width={AVATAR_OPTIONS.width}
          height={AVATAR_OPTIONS.height}
          options={AVATAR_OPTIONS}
          className={CSS.experienceCardAvatar}
          src={photo}
        />
      </div>
    );
  }

  return (
    <OriginalsAvatars
      experience={experience}
      label={label}
      forceHost={forceHost}
    />
  );
}

export default ExperienceCardAvatar;
